"use client";

import { isValidElement, useState, useEffect, useCallback, useRef } from "react";
import { Copy, Maximize2, X } from "lucide-react";
import { codeToHtml } from "shiki";
import { extractText } from "../lib/extractText";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "./ui/dialog";
import { useEffectivePass } from "../hooks/useEffectivePass";

import { useBrowserPreference } from "../hooks/useBrowserPreference";
import { useExternalLearningSettings } from "./learning-settings-context";

const PLAIN_TEXT_KEY = "code-plain-text";
const CODE_ZOOM_HISTORY_KEY = "__jcgCodeZoom";
let restoringStaleCodeZoomHistoryId: string | null = null;

const DEFAULT_EXPANDED_VIEWPORT = {
  left: "0px",
  top: "0px",
  width: "100dvw",
  height: "100dvh",
};

type CodeZoomHistoryPhase = "return" | "modal";

interface CodeZoomHistoryMarker {
  id: string;
  phase: CodeZoomHistoryPhase;
  originalState?: unknown;
}

interface CodeZoomHistoryEntry {
  id: string;
  originalState: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function createCodeZoomHistoryId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function withCodeZoomMarker(
  state: unknown,
  marker: CodeZoomHistoryMarker,
): Record<string, unknown> {
  return {
    ...(isRecord(state) ? state : {}),
    [CODE_ZOOM_HISTORY_KEY]: marker,
  };
}

function getCodeZoomMarker(state: unknown): CodeZoomHistoryMarker | null {
  if (!isRecord(state)) return null;

  const marker = state[CODE_ZOOM_HISTORY_KEY];
  if (!isRecord(marker)) return null;

  const { id, phase } = marker;
  if (
    typeof id === "string" &&
    (phase === "return" || phase === "modal")
  ) {
    return {
      id,
      phase,
      originalState: marker.originalState,
    };
  }

  return null;
}

function isCodeZoomHistoryState(
  state: unknown,
  id: string,
  phase: CodeZoomHistoryPhase,
): boolean {
  const marker = getCodeZoomMarker(state);
  return marker?.id === id && marker.phase === phase;
}

interface CodeBlockProps {
  children: React.ReactNode;
  inProblem?: boolean;
  [key: string]: unknown;
}

export default function CodeBlock({ children, inProblem, ...props }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedViewport, setExpandedViewport] = useState(
    DEFAULT_EXPANDED_VIEWPORT,
  );
  const [highlightedHtml, setHighlightedHtml] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [plainTextPref, setPlainText] = useBrowserPreference(PLAIN_TEXT_KEY, false);
  const externalSettings = useExternalLearningSettings();
  const codeZoomHistoryRef = useRef<CodeZoomHistoryEntry | null>(null);
  const { isPremiumOrAbove } = useEffectivePass();

  const showExamMode = inProblem && isPremiumOrAbove;
  const plainText = showExamMode && plainTextPref;

  useEffect(() => {
    const marker = getCodeZoomMarker(window.history.state);
    if (!marker) return;

    if (marker.phase === "return") {
      window.history.replaceState(
        marker.originalState ?? null,
        "",
        window.location.href,
      );
      return;
    }

    if (restoringStaleCodeZoomHistoryId === marker.id) return;
    restoringStaleCodeZoomHistoryId = marker.id;

    const restoreStaleModalEntry = () => {
      const nextMarker = getCodeZoomMarker(window.history.state);
      if (!nextMarker || nextMarker.id !== marker.id) {
        if (restoringStaleCodeZoomHistoryId === marker.id) {
          restoringStaleCodeZoomHistoryId = null;
        }
        return;
      }

      window.history.replaceState(
        nextMarker.originalState ?? marker.originalState ?? null,
        "",
        window.location.href,
      );
      if (restoringStaleCodeZoomHistoryId === marker.id) {
        restoringStaleCodeZoomHistoryId = null;
      }
    };

    window.history.back();
    window.setTimeout(restoreStaleModalEntry, 500);
  }, []);

  const restoreCodeZoomHistoryState = useCallback(
    (entry: CodeZoomHistoryEntry) => {
      if (
        typeof window === "undefined" ||
        !isCodeZoomHistoryState(window.history.state, entry.id, "return")
      ) {
        return;
      }

      window.history.replaceState(entry.originalState, "", window.location.href);
    },
    [],
  );

  const openExpandedCode = useCallback(() => {
    setIsExpanded(true);

    if (typeof window === "undefined") return;

    const currentEntry = codeZoomHistoryRef.current;
    if (
      currentEntry &&
      isCodeZoomHistoryState(window.history.state, currentEntry.id, "modal")
    ) {
      return;
    }

    const id = createCodeZoomHistoryId();
    const originalState = window.history.state;

    try {
      window.history.replaceState(
        withCodeZoomMarker(originalState, {
          id,
          phase: "return",
          originalState,
        }),
        "",
        window.location.href,
      );
      window.history.pushState(
        withCodeZoomMarker(originalState, {
          id,
          phase: "modal",
          originalState,
        }),
        "",
        window.location.href,
      );
      codeZoomHistoryRef.current = { id, originalState };
    } catch (error) {
      console.warn("코드 확대 history 상태 추가 실패:", error);
      codeZoomHistoryRef.current = null;
    }
  }, []);

  const closeExpandedCode = useCallback(() => {
    setIsExpanded(false);

    if (typeof window === "undefined") return;

    const entry = codeZoomHistoryRef.current;
    if (!entry) return;

    if (isCodeZoomHistoryState(window.history.state, entry.id, "modal")) {
      const handleBackToReturnState = (event: PopStateEvent) => {
        if (!isCodeZoomHistoryState(event.state, entry.id, "return")) {
          return;
        }

        restoreCodeZoomHistoryState(entry);
        if (codeZoomHistoryRef.current?.id === entry.id) {
          codeZoomHistoryRef.current = null;
        }
        window.removeEventListener("popstate", handleBackToReturnState);
      };

      window.addEventListener("popstate", handleBackToReturnState);
      window.history.back();

      window.setTimeout(() => {
        window.removeEventListener("popstate", handleBackToReturnState);
        restoreCodeZoomHistoryState(entry);
        if (isCodeZoomHistoryState(window.history.state, entry.id, "modal")) {
          window.history.replaceState(
            entry.originalState,
            "",
            window.location.href,
          );
        }
        if (codeZoomHistoryRef.current?.id === entry.id) {
          codeZoomHistoryRef.current = null;
        }
      }, 500);
      return;
    }

    restoreCodeZoomHistoryState(entry);
    codeZoomHistoryRef.current = null;
  }, [restoreCodeZoomHistoryState]);

  const handleExpandedOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        openExpandedCode();
        return;
      }

      closeExpandedCode();
    },
    [closeExpandedCode, openExpandedCode],
  );

  useEffect(() => {
    if (!isExpanded) return;

    const handlePopState = (event: PopStateEvent) => {
      const entry = codeZoomHistoryRef.current;
      if (
        !entry ||
        !isCodeZoomHistoryState(event.state, entry.id, "return")
      ) {
        return;
      }

      restoreCodeZoomHistoryState(entry);
      codeZoomHistoryRef.current = null;
      setIsExpanded(false);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isExpanded, restoreCodeZoomHistoryState]);

  useEffect(() => {
    return () => {
      const entry = codeZoomHistoryRef.current;
      if (!entry || typeof window === "undefined") return;

      if (
        isCodeZoomHistoryState(window.history.state, entry.id, "return") ||
        isCodeZoomHistoryState(window.history.state, entry.id, "modal")
      ) {
        window.history.replaceState(
          entry.originalState,
          "",
          window.location.href,
        );
      }

      codeZoomHistoryRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!isExpanded) return;

    const updateExpandedViewport = () => {
      const viewport = window.visualViewport;
      if (!viewport) {
        setExpandedViewport(DEFAULT_EXPANDED_VIEWPORT);
        return;
      }

      setExpandedViewport({
        left: `${viewport.offsetLeft}px`,
        top: `${viewport.offsetTop}px`,
        width: `${viewport.width}px`,
        height: `${viewport.height}px`,
      });
    };

    updateExpandedViewport();

    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", updateExpandedViewport);
    viewport?.addEventListener("scroll", updateExpandedViewport);
    window.addEventListener("resize", updateExpandedViewport);
    window.addEventListener("orientationchange", updateExpandedViewport);

    return () => {
      viewport?.removeEventListener("resize", updateExpandedViewport);
      viewport?.removeEventListener("scroll", updateExpandedViewport);
      window.removeEventListener("resize", updateExpandedViewport);
      window.removeEventListener("orientationchange", updateExpandedViewport);
    };
  }, [isExpanded]);

  // 언어 정보와 코드 텍스트 추출
  let language = "text";
  let codeText = "";

  // 1. props에서 미리 추출된 코드 텍스트 확인 (MDX에서 전달된 것)
  if (
    "data-code-text" in props &&
    typeof props["data-code-text"] === "string" &&
    props["data-code-text"].trim()
  ) {
    codeText = props["data-code-text"] as string;
  }

  // 언어 정보 추출
  // 1. props에서 data-language 확인
  if ("data-language" in props && typeof props["data-language"] === "string") {
    language = props["data-language"] as string;
  }

  // 2. props className에서 언어 확인 (language-xxx 패턴)
  else if ("className" in props && typeof props.className === "string") {
    const className = props.className as string;
    const langMatch = className.match(/language-(\w+)/);
    if (langMatch) {
      language = langMatch[1];
    }
  }

  // 3. children props에서 확인 (fallback)
  else if (isValidElement(children) && children.props) {
    const childProps = children.props as Record<string, unknown>;

    if (
      "data-language" in childProps &&
      typeof childProps["data-language"] === "string"
    ) {
      language = childProps["data-language"];
    } else if (
      "className" in childProps &&
      typeof childProps.className === "string"
    ) {
      const className = childProps.className;
      const langMatch = className.match(/language-(\w+)/);
      if (langMatch) {
        language = langMatch[1];
      }
    }
  }

  const languageLabel = language === "output" ? "출력" : language;

  // 공통 extractText 함수를 사용

  // props에서 코드 텍스트가 없을 때만 children에서 추출
  if (!codeText) {
    // 더 안전한 텍스트 추출
    try {
      if (
        isValidElement(children) &&
        children.props &&
        typeof children.props === "object" &&
        children.props !== null &&
        "children" in children.props
      ) {
        codeText = extractText(children.props.children as React.ReactNode);
      } else {
        codeText = extractText(children);
      }

      // 텍스트가 비어있거나 너무 짧다면 대체 방법들 시도
      if (!codeText.trim() || codeText.length < 50) {
        // 방법 1: 직접 String 변환 (React 요소면 [object Object]가 되므로 제외)
        const fallback1 = isValidElement(children) ? "" : String(children);

        // 방법 2: React 요소 배열 직접 순회하여 텍스트 추출
        if (
          isValidElement(children) &&
          children.props &&
          typeof children.props === "object" &&
          "children" in children.props
        ) {
          try {
            const deepExtractText = (node: React.ReactNode): string => {
              if (typeof node === "string") return node;
              if (typeof node === "number") return String(node);
              if (node === null || node === undefined) return "";

              if (Array.isArray(node)) {
                return node.map(deepExtractText).join("");
              }

              if (isValidElement(node)) {
                const nodeProps = node.props as Record<string, unknown>;
                if (
                  nodeProps &&
                  typeof nodeProps === "object" &&
                  "children" in nodeProps
                ) {
                  return deepExtractText(nodeProps.children as React.ReactNode);
                }
              }

              return "";
            };

            const childrenData = children.props.children as React.ReactNode;
            if (!Array.isArray(childrenData)) {
              const singleExtracted = deepExtractText(childrenData);
              if (singleExtracted && singleExtracted.length > codeText.length) {
                codeText = singleExtracted;
              }
            } else {
              const childrenArray = childrenData as React.ReactNode[];
              const deepExtractedParts: string[] = [];

              childrenArray.forEach((child) => {
                const extracted = deepExtractText(child);
                if (extracted) {
                  deepExtractedParts.push(extracted);
                }
              });

              const deepExtractedText = deepExtractedParts.join("");
              if (deepExtractedText.length > codeText.length) {
                codeText = deepExtractedText;
              }
            }
          } catch {
            // 깊은 추출 실패 시 무시
          }
        }

        if (fallback1.length > codeText.length) {
          codeText = fallback1;
        }
      }
    } catch (error) {
      console.error("텍스트 추출 오류:", error);
      codeText = isValidElement(children) ? extractText(children) : String(children);
    }
  }

  // Shiki를 사용한 syntax highlighting
  useEffect(() => {
    const highlightCode = async () => {
      try {
        setIsLoading(true);

        const html = await codeToHtml(codeText, {
          lang: language === "text" || language === "output" ? "txt" : language,
          themes: {
            light: "github-light",
            dark: "github-dark",
          },
        });

        setHighlightedHtml(html);
      } catch (error) {
        console.error("Shiki highlighting error:", error);
        // fallback: 원본 코드를 그대로 표시
        setHighlightedHtml(`<pre><code>${codeText}</code></pre>`);
      } finally {
        setIsLoading(false);
      }
    };

    if (codeText) {
      highlightCode();
    }
  }, [codeText, language]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("복사 실패:", err);
    }
  };

  const renderedCode = isLoading && !plainText ? (
    <div className="p-4 text-gray-500 dark:text-gray-400">
      코드 하이라이팅 중…
    </div>
  ) : plainText ? (
    <pre className="!p-4 text-sm !mb-0 whitespace-pre overflow-x-auto !bg-white dark:!bg-gray-800 text-black dark:text-gray-100">
      <code>{codeText}</code>
    </pre>
  ) : (
    <div
      dangerouslySetInnerHTML={{ __html: highlightedHtml }}
      className="[&>pre]:!p-4 [&>pre]:text-sm [&>pre]:!mb-0 [&>pre]:!max-h-none [&>pre]:!h-auto [&>pre]:whitespace-pre [&>pre]:overflow-x-auto"
    />
  );

  const expandedCode = isLoading && !plainText ? (
    <div className="p-4 text-sm text-muted-foreground">
      코드 하이라이팅 중…
    </div>
  ) : plainText ? (
    <pre className="min-w-max !p-4 text-sm !mb-0 whitespace-pre !bg-white text-black dark:!bg-gray-800 dark:text-gray-100">
      <code>{codeText}</code>
    </pre>
  ) : (
    <div
      dangerouslySetInnerHTML={{ __html: highlightedHtml }}
      className="min-w-max [&>pre]:min-w-max [&>pre]:!p-4 [&>pre]:text-sm [&>pre]:!mb-0 [&>pre]:!max-h-none [&>pre]:!h-auto [&>pre]:whitespace-pre [&>pre]:overflow-visible"
    />
  );

  return (
    <>
      <div
        className="border border-gray-300 bg-neutral-50 rounded-lg overflow-hidden my-4 dark:bg-gray-700/50 dark:border-gray-600"
        data-llm-code-block="true"
        data-llm-code-language={language}
        data-llm-code-text={codeText}
      >
        {/* 언어 라벨 및 버튼 */}
        <div
          className="bg-[#ECEAEA] px-3 py-1 text-sm text-[#706E6E] border-b border-gray-300 dark:bg-gray-800 dark:text-neutral-200 dark:border-gray-600 flex justify-between items-center"
          data-llm-copy-control="true"
        >
          <span>{languageLabel}</span>
          <div className="flex items-center gap-1">
            {showExamMode && !externalSettings && (
              <button
                type="button"
                onClick={() => setPlainText(!plainTextPref)}
                className={`min-h-8 px-2.5 py-1.5 text-xs border rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
                  plainText
                    ? "bg-gray-700 text-white border-gray-700 dark:bg-gray-200 dark:text-gray-800 dark:border-gray-200"
                    : "bg-white border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600"
                }`}
                title="시험처럼 색 구분 없이 보기"
              >
                시험장 코드
              </button>
            )}
            <button
              type="button"
              onClick={openExpandedCode}
              className="inline-flex sm:hidden min-h-8 w-8 touch-manipulation items-center justify-center rounded border border-gray-300 bg-white text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
              aria-label="코드 크게 보기"
              title="코드 크게 보기"
            >
              <Maximize2 className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={handleCopy}
              className="min-h-8 px-2.5 py-1.5 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
              title="코드 복사"
            >
              {copied ? "복사됨!" : "복사"}
            </button>
          </div>
        </div>

        {/* 코드 영역 */}
        <div
          className="text-sm [&>pre]:whitespace-pre [&>pre]:overflow-x-auto"
          translate="no"
        >
          {renderedCode}
        </div>
      </div>

      <Dialog open={isExpanded} onOpenChange={handleExpandedOpenChange}>
        <DialogContent
          className="!fixed !translate-x-0 !translate-y-0 !max-w-none !max-h-none sm:!max-w-none p-0 gap-0 rounded-none border-none bg-background text-foreground shadow-none overflow-hidden overscroll-contain"
          style={expandedViewport}
          showCloseButton={false}
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">
            {language === "output" ? "출력 크게 보기" : `${language} 코드 크게 보기`}
          </DialogTitle>

          <div className="flex h-full min-h-0 min-w-0 flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
            <div className="flex h-12 min-w-0 shrink-0 items-center gap-2 border-b border-border bg-muted/60 px-3">
              <span className="min-w-0 shrink truncate text-sm font-medium text-foreground">
                {languageLabel}
              </span>
              <span className="mx-2 hidden min-w-0 flex-1 truncate text-center text-xs text-muted-foreground portrait:block landscape:hidden">
                가로로 돌리면 더 넓게 볼 수 있어요
              </span>
              <div className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex h-9 w-9 touch-manipulation items-center justify-center rounded-md border border-border bg-background text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                  aria-label={copied ? "코드 복사됨" : "코드 복사"}
                  title={copied ? "복사됨!" : "코드 복사"}
                >
                  <Copy className="h-4 w-4" aria-hidden />
                  <span className="sr-only" aria-live="polite">
                    {copied ? "코드가 복사되었습니다" : "코드 복사"}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={closeExpandedCode}
                  className="inline-flex h-9 w-9 touch-manipulation items-center justify-center rounded-md border border-border bg-background text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                  aria-label="닫기"
                  title="닫기"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </div>

            <div
              className="min-h-0 min-w-0 flex-1 overflow-auto overscroll-contain bg-background text-sm"
              translate="no"
            >
              {expandedCode}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
